import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Paper,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Avatar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SchoolIcon from '@mui/icons-material/School';

export default function CourseForm({ course, onSave, onCancel, loading }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    level: '',
    language: 'pt-BR',
    status: 'DRAFT',
  });

  useEffect(() => {
    if (course) {
      setFormData({
        title: course.title || '',
        description: course.description || '',
        level: course.level || '',
        language: course.language || 'pt-BR',
        status: course.status || 'DRAFT',
      });
    }
  }, [course]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    onSave(formData);
  };

  const levelOptions = [
    { value: 'Básico', label: 'Básico', color: '#22c55e' },
    { value: 'Intermediário', label: 'Intermediário', color: '#f59e0b' },
    { value: 'Avançado', label: 'Avançado', color: '#ef4444' },
  ];

  const statusOptions = [
    { value: 'DRAFT', label: 'Rascunho', color: '#6b7280' },
    { value: 'PUBLISHED', label: 'Publicado', color: '#059669' },
    { value: 'ARCHIVED', label: 'Arquivado', color: '#dc2626' },
  ];

  return (
    <>
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
            <SchoolIcon />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {course ? 'Editar Curso' : 'Novo Curso'}
          </Typography>
        </Box>
        <IconButton
          onClick={onCancel}
          sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Paper
          component="form"
          onSubmit={handleSubmit}
          sx={{
            p: 4,
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
            minHeight: 500,
            border: '1px solid rgba(59, 130, 246, 0.1)',
          }}
        >
          <Box sx={{ display: 'grid', gap: 3 }}>
            {/* Título */}
            <TextField
              fullWidth
              label="Título do Curso"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                  borderRadius: '12px',
                  '& input': {
                    color: '#ffffff',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                    borderWidth: '2px',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#3b82f6',
                },
              }}
            />

            {/* Descrição */}
            <TextField
              fullWidth
              label="Descrição"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={4}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                  borderRadius: '12px',
                  '& textarea': {
                    color: '#ffffff',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                    borderWidth: '2px',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#3b82f6',
                },
              }}
            />

            <Box
              sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}
            >
              {/* Nível */}
              <FormControl fullWidth>
                <InputLabel
                  sx={{ '&.Mui-focused': { color: '#3b82f6' }, color: 'rgba(255, 255, 255, 0.7)' }}
                >
                  Nível
                </InputLabel>
                <Select
                  name="level"
                  value={formData.level}
                  onChange={handleChange}
                  label="Nível"
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#ffffff',
                    borderRadius: '12px',
                    '& .MuiSvgIcon-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3b82f6',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3b82f6',
                      borderWidth: '2px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(59, 130, 246, 0.2)',
                    },
                  }}
                >
                  {levelOptions.map(option => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={{ backgroundColor: '#1e293b', color: '#ffffff' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: option.color,
                          }}
                        />
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Idioma */}
              <TextField
                fullWidth
                label="Idioma"
                name="language"
                value={formData.language}
                onChange={handleChange}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#ffffff',
                    borderRadius: '12px',
                    '& input': {
                      color: '#ffffff',
                    },
                    '&:hover fieldset': {
                      borderColor: '#3b82f6',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3b82f6',
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#3b82f6',
                  },
                }}
              />
            </Box>

            {/* Status */}
            <FormControl fullWidth>
              <InputLabel
                sx={{ '&.Mui-focused': { color: '#3b82f6' }, color: 'rgba(255, 255, 255, 0.7)' }}
              >
                Status
              </InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleChange}
                label="Status"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                  borderRadius: '12px',
                  '& .MuiSvgIcon-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3b82f6',
                    borderWidth: '2px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(59, 130, 246, 0.2)',
                  },
                }}
              >
                {statusOptions.map(option => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{ backgroundColor: '#1e293b', color: '#ffffff' }}
                  >
                    <Chip
                      label={option.label}
                      size="small"
                      sx={{
                        backgroundColor: option.color,
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          borderTop: '1px solid rgba(59, 130, 246, 0.1)',
          gap: 2,
        }}
      >
        <Button
          onClick={onCancel}
          variant="outlined"
          sx={{
            borderColor: 'rgba(59, 130, 246, 0.5)',
            color: '#3b82f6',
            borderRadius: '12px',
            px: 3,
            py: 1,
            fontWeight: 600,
            '&:hover': {
              borderColor: '#3b82f6',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
            },
          }}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          onClick={handleSubmit}
          sx={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
            borderRadius: '12px',
            px: 4,
            py: 1,
            fontWeight: 600,
            boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #2563eb 0%, #0891b2 100%)',
              boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)',
            },
            '&:disabled': {
              background: '#475569',
              color: '#94a3b8',
            },
          }}
        >
          {loading ? 'Salvando...' : 'Salvar Curso'}
        </Button>
      </DialogActions>
    </>
  );
}
